import { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import CommonModal from "../modalLayout/CommonModal";
import AudiologistDeliveryFields from "../../va/AudiologistDeliveryFields";
import {
  audiologistDeliveryToOrderPatch,
  isAudiologistChannel,
  isVaChannel,
  normalizePoForCompare,
  orderToAudiologistDeliveryForm,
  orderToShippingEditForm,
  shippingEditFormToOrderPatch,
} from "../../../helpers/vaInvoiceUtils";
import "../../../styles/vaOrderModal.css";

const emptyForm = {
  fullName: "",
  email: "",
  facilityName: "",
  attnPo: "",
  recipient: "",
  address1: "",
  address2: "",
  city: "",
  state: "",
  zip: "",
  country: "",
  phone: "",
};

const emptyAudiologistForm = {
  firstName: "",
  lastName: "",
  address1: "",
  address2: "",
  city: "",
  state: "",
  zip: "",
  phone: "",
  email: "",
  shippingEmail: "",
};

export function orderToCustomerForm(order = {}) {
  return {
    fullName: order.customerName || "",
    email: order.customerEmail || "",
    ...orderToShippingEditForm(order),
  };
}

function normalizeFieldValue(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

function normalizeCountryCode(value) {
  const v = normalizeFieldValue(value).toUpperCase();
  if (
    v === "US" ||
    v === "USA" ||
    v === "UNITED STATES" ||
    v === "UNITED STATES OF AMERICA"
  ) {
    return "US";
  }
  return v;
}

function fieldValuesEqual(field, nextValue, orderValue, order = {}) {
  if (field === "invoicePoNumber") {
    return (
      normalizePoForCompare(nextValue) === normalizePoForCompare(orderValue)
    );
  }

  // Country often lives on AddressCountryCode for webhook orders while
  // ShippingCountry is empty — don't treat filling US as a real edit.
  if (field === "ShippingCountry") {
    return (
      normalizeCountryCode(nextValue) ===
      normalizeCountryCode(orderValue || order.AddressCountryCode)
    );
  }

  // Province code may be derived from ShippingProvince (e.g. FL) when the
  // code field was never stored — don't log a backfill as a change.
  if (field === "ShippingProvinceCode") {
    const next = normalizeFieldValue(nextValue).toUpperCase();
    const existingCode = normalizeFieldValue(orderValue).toUpperCase();
    const existingProvince = normalizeFieldValue(
      order.ShippingProvince
    ).toUpperCase();
    return next === existingCode || next === existingProvince;
  }

  if (field === "ShippingProvince") {
    const next = normalizeFieldValue(nextValue).toUpperCase();
    const existingProvince = normalizeFieldValue(orderValue).toUpperCase();
    const existingCode = normalizeFieldValue(
      order.ShippingProvinceCode
    ).toUpperCase();
    return next === existingProvince || next === existingCode;
  }

  return normalizeFieldValue(orderValue) === normalizeFieldValue(nextValue);
}

const SHIPPING_PATCH_FIELDS = new Set([
  "ShippingName",
  "Address1",
  "Address2",
  "ShippingCity",
  "ShippingProvince",
  "ShippingProvinceCode",
  "ShippingZip",
  "ShippingCountry",
  "ShippingPhone",
  "vaClinicName",
  "invoicePoNumber",
]);

function changedFieldsFrom(next, order = {}) {
  const changed = {};
  for (const [key, value] of Object.entries(next)) {
    if (key === "vaShipToRaw") continue; // only add if another shipping field changed
    if (!fieldValuesEqual(key, value, order[key], order)) {
      changed[key] = value;
    }
  }

  const shippingChanged = Object.keys(changed).some((key) =>
    SHIPPING_PATCH_FIELDS.has(key)
  );
  if (shippingChanged && Object.prototype.hasOwnProperty.call(next, "vaShipToRaw")) {
    if (!fieldValuesEqual("vaShipToRaw", next.vaShipToRaw, order.vaShipToRaw, order)) {
      changed.vaShipToRaw = next.vaShipToRaw;
    }
  }

  return changed;
}

/** Map modal form → order fields, including only values that differ from `order`. */
export function customerFormToOrderFields(
  form,
  order = {},
  { includeCustomerIdentity = false } = {}
) {
  if (isAudiologistChannel(order)) {
    return changedFieldsFrom(
      audiologistDeliveryToOrderPatch(form, { includeCustomerIdentity }),
      order
    );
  }

  const next = shippingEditFormToOrderPatch(form, order, {
    includeCustomerIdentity,
  });
  return changedFieldsFrom(next, order);
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

function getAudiologistInvalidFields(form) {
  const invalid = {};
  ["firstName", "lastName", "address1", "city", "state", "zip", "phone", "email"].forEach(
    (name) => {
      if (!String(form[name] ?? "").trim()) {
        invalid[name] = true;
      }
    }
  );
  if (!invalid.email && form.email?.trim() && !isValidEmail(form.email)) {
    invalid.email = true;
  }
  if (form.shippingEmail?.trim() && !isValidEmail(form.shippingEmail)) {
    invalid.shippingEmail = true;
  }
  return invalid;
}

function getInvalidFields(
  form,
  {
    isVaOrder = false,
    allowEditIdentity = false,
    shippingLocked = false,
    allowEditName = false,
  } = {}
) {
  const invalid = {};

  const requireIdentity = !isVaOrder || allowEditIdentity || allowEditName;
  const requireShipping = !shippingLocked;
  const required = [];

  if (requireIdentity) {
    required.push("fullName");
  }
  if (!isVaOrder || allowEditIdentity) {
    required.push("email");
  }
  if (requireShipping) {
    required.push("address1", "zip", "phone");
  }

  required.forEach((name) => {
    if (!String(form[name] ?? "").trim()) {
      invalid[name] = true;
    }
  });

  if (
    (!isVaOrder || allowEditIdentity) &&
    !invalid.email &&
    form.email?.trim()
  ) {
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
    if (!emailOk) {
      invalid.email = true;
    }
  }

  return invalid;
}

export default function EditCustomerModal({
  show,
  setShow,
  order,
  onSave,
  allowEditIdentity = false,
  allowEditName = false,
  shippingLocked = false,
  confirmLoading = false,
}) {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const isAudiologistOrder = isAudiologistChannel(order);
  const isVaOrder = isVaChannel(order);
  const canEditName = allowEditName || allowEditIdentity || !isVaOrder;
  const identityLocked = isVaOrder && !allowEditIdentity;
  const nameLocked = isVaOrder && !canEditName;
  const shippingFieldsLocked = Boolean(shippingLocked) && isVaOrder;

  useEffect(() => {
    if (show && order) {
      setForm(
        isAudiologistChannel(order)
          ? { ...emptyAudiologistForm, ...orderToAudiologistDeliveryForm(order) }
          : orderToCustomerForm(order)
      );
      setErrors({});
    }
  }, [show, order]);

  const clearFieldError = (name) => {
    if (!errors[name]) return;
    setErrors((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    clearFieldError(name);
  };

  const handlePlaceSelect = (place) => {
    if (shippingFieldsLocked) return;
    setForm((prev) => ({
      ...prev,
      address1: place.address1 ?? prev.address1,
      city: place.city ?? prev.city,
      state: place.state ?? prev.state,
      zip: place.zip ?? prev.zip,
    }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next.address1;
      delete next.city;
      delete next.state;
      delete next.zip;
      return next;
    });
  };

  const handleConfirm = async () => {
    if (confirmLoading) return;
    const nextErrors = isAudiologistOrder
      ? getAudiologistInvalidFields(form)
      : getInvalidFields(form, {
          isVaOrder,
          allowEditIdentity,
          shippingLocked: shippingFieldsLocked,
          allowEditName: canEditName,
        });
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    let fields = customerFormToOrderFields(form, order, {
      includeCustomerIdentity: allowEditIdentity || !isVaOrder,
    });

    // VA distributors can update Name even when identity (email) stays locked.
    if (canEditName && isVaOrder) {
      const nextName = String(form.fullName || "").trim();
      const prevName = String(
        order?.patientName || order?.customerName || ""
      ).trim();
      if (nextName && nextName !== prevName) {
        fields = {
          ...fields,
          customerName: nextName,
          ...(isVaOrder ? { patientName: nextName } : {}),
        };
      }
    }

    if (shippingFieldsLocked) {
      const identityOnly = {};
      if (Object.prototype.hasOwnProperty.call(fields, "customerName")) {
        identityOnly.customerName = fields.customerName;
      }
      if (Object.prototype.hasOwnProperty.call(fields, "patientName")) {
        identityOnly.patientName = fields.patientName;
      }
      if (
        allowEditIdentity &&
        Object.prototype.hasOwnProperty.call(fields, "customerEmail")
      ) {
        identityOnly.customerEmail = fields.customerEmail;
      }
      fields = identityOnly;
    }

    if (!fields || Object.keys(fields).length === 0) {
      setShow(false);
      return;
    }

    await onSave?.(fields);
  };

  const inputClass = (name) =>
    `delivery-summary__input${errors[name] ? " border border-danger" : ""}`;

  const shippingInputProps = shippingFieldsLocked
    ? { readOnly: true, disabled: true }
    : {};

  return (
    <CommonModal
      show={show}
      setShow={setShow}
      title="Edit customer information"
      className={
        isAudiologistOrder
          ? "order-confirmation-modal modal-md"
          : "modal-md"
      }
      centered
      onConfirm={handleConfirm}
      confirmLoading={confirmLoading}
      confirmDisabled={confirmLoading}
      cancelDisabled={confirmLoading}
    >
      {isAudiologistOrder ? (
        <div className="order-confirmation">
          <AudiologistDeliveryFields
            title="Shipping address"
            showEmail
            emailName="shippingEmail"
            delivery={form}
            errors={errors}
            onChange={handleChange}
            onPlaceSelect={handlePlaceSelect}
          />
          <div className="order-confirmation__delivery">
            <h6 className="order-confirmation__section-title">
              Contact Information
            </h6>
            <Form.Control
              className={`cmn_input${errors.email ? " border border-danger" : ""}`}
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="Enter email address"
            />
          </div>
        </div>
      ) : (
      <div className="delivery-summary delivery-summary--editing">
        <h6 className="delivery-summary__title mb-3">Name</h6>
        <div className="row g-2 delivery-summary__form mb-4">
          <div className="col-12">
            <Form.Control
              className={inputClass("fullName")}
              name="fullName"
              value={form.fullName}
              onChange={handleChange}
              placeholder="Full name"
              required
              readOnly={nameLocked}
              disabled={nameLocked}
            />
          </div>
        </div>

        <h6 className="delivery-summary__title mb-3">Shipping Address</h6>
        {shippingFieldsLocked && (
          <p className="text-muted small mb-2">
            Shipping address cannot be changed after the order has shipped.
          </p>
        )}
        <div className="row g-2 delivery-summary__form mb-4">
          {isVaOrder && (
            <>
              <div className="col-12">
                <Form.Control
                  className={inputClass("facilityName")}
                  name="facilityName"
                  value={form.facilityName}
                  onChange={handleChange}
                  placeholder="Facility / clinic name"
                  {...shippingInputProps}
                />
              </div>
              <div className="col-12">
                <Form.Control
                  className={inputClass("attnPo")}
                  name="attnPo"
                  value={form.attnPo}
                  onChange={handleChange}
                  placeholder="ATTN PO: …"
                  {...shippingInputProps}
                />
              </div>
            </>
          )}
          <div className="col-12">
            <Form.Control
              className={inputClass("recipient")}
              name="recipient"
              value={form.recipient}
              onChange={handleChange}
              placeholder="Recipient"
              {...shippingInputProps}
            />
          </div>
          <div className="col-12">
            <Form.Control
              className={inputClass("address1")}
              name="address1"
              value={form.address1}
              onChange={handleChange}
              placeholder="Street address"
              required
              {...shippingInputProps}
            />
          </div>
          <div className="col-12">
            <Form.Control
              className={inputClass("address2")}
              name="address2"
              value={form.address2}
              onChange={handleChange}
              placeholder="Apartment, suite, floor"
              {...shippingInputProps}
            />
          </div>
          <div className="col-12 col-md-5">
            <Form.Control
              className={inputClass("city")}
              name="city"
              value={form.city}
              onChange={handleChange}
              placeholder="City"
              {...shippingInputProps}
            />
          </div>
          <div className="col-6 col-md-3">
            <Form.Control
              className={inputClass("state")}
              name="state"
              value={form.state}
              onChange={handleChange}
              placeholder="State"
              maxLength={2}
              {...shippingInputProps}
            />
          </div>
          <div className="col-6 col-md-4">
            <Form.Control
              className={inputClass("zip")}
              name="zip"
              value={form.zip}
              onChange={handleChange}
              placeholder="ZIP"
              required
              {...shippingInputProps}
            />
          </div>
          <div className="col-12">
            <Form.Control
              className={inputClass("country")}
              name="country"
              value={form.country}
              onChange={handleChange}
              placeholder="Country"
              {...shippingInputProps}
            />
          </div>
          <div className="col-12">
            <Form.Control
              className={inputClass("phone")}
              name="phone"
              value={form.phone}
              onChange={handleChange}
              placeholder="Phone"
              required
              {...shippingInputProps}
            />
          </div>
        </div>

        <h6 className="delivery-summary__title mb-3">Contact Information</h6>
        <div className="row g-2 delivery-summary__form">
          <div className="col-12">
            <Form.Control
              className={inputClass("email")}
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="Email"
              required
              readOnly={identityLocked}
              disabled={identityLocked}
            />
          </div>
        </div>
      </div>
      )}
    </CommonModal>
  );
}
